import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, BookOpen, ListChecks } from "lucide-react";
import { META, WORKSHEETS } from "../data/ks2_workbook";

function TaskBlock({ task, idx }) {
  const num = idx + 1;
  const tag = task.type;
  const tagClass = {
    WRITE: "bg-saffron/15 text-saffron border-saffron/30",
    DRAW: "bg-blue-100 text-blue-800 border-blue-200",
    MATCH: "bg-green-100 text-green-800 border-green-200",
    CIRCLE: "bg-purple-100 text-purple-800 border-purple-200",
    TICK: "bg-amber-100 text-amber-800 border-amber-200",
  }[tag] || "bg-navy/10 text-navy border-navy/15";

  return (
    <div className="border-l-4 border-saffron/50 pl-4 py-3 break-inside-avoid">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-navy text-pearl text-sm font-display flex items-center justify-center">
          {num}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 rounded ${tagClass}`}>
              {tag}
            </span>
          </div>
          <p className="mt-2 text-navy leading-snug" dangerouslySetInnerHTML={{ __html: task.prompt }} />

          {task.type === "WRITE" && (
            <div className="mt-3 space-y-2.5">
              {Array.from({ length: task.lines || 2 }).map((_, i) => (
                <div key={i} className="border-b border-dashed border-navy/30 h-6" />
              ))}
            </div>
          )}

          {task.type === "DRAW" && (
            <div className="mt-3 border-2 border-dashed border-navy/30 rounded-md h-40 flex items-center justify-center text-navy/30 text-xs italic">
              draw here
            </div>
          )}

          {task.type === "TICK" && (
            <ul className="mt-3 space-y-2">
              {(task.items || []).map((it) => (
                <li key={it} className="flex items-start gap-3">
                  <span className="inline-block w-5 h-5 border border-navy/40 rounded-sm flex-shrink-0 mt-0.5" />
                  <span className="text-navy/85 leading-snug">{it}</span>
                </li>
              ))}
            </ul>
          )}

          {task.type === "CIRCLE" && (
            <div className="mt-3 flex flex-wrap gap-3">
              {(task.items || []).map((it) => (
                <span
                  key={it}
                  className="px-3 py-1.5 border border-navy/30 rounded-full text-navy/85 text-sm"
                >
                  {it}
                </span>
              ))}
            </div>
          )}

          {task.type === "MATCH" && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-1.5">A</div>
                {(task.items || []).map(([left]) => (
                  <div key={left} className="border border-navy/15 rounded-md p-2.5 text-sm text-navy/85 mb-2">
                    {left}
                  </div>
                ))}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-navy/50 mb-1.5">B</div>
                {(task.items || []).map(([, right], i) => (
                  <div key={i} className="border border-navy/15 rounded-md p-2.5 text-sm text-navy/85 mb-2">
                    {right}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Ks2WorkbookViewer() {
  const [active, setActive] = useState(WORKSHEETS[0].n);
  const current = WORKSHEETS.find((w) => w.n === active) || WORKSHEETS[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [active]);

  return (
    <div className="bg-pearl min-h-screen" data-testid="ks2-workbook">
      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-section { page-break-after: always; }
          body { background: white; }
        }
      `}</style>

      <header className="bg-navy text-pearl py-10 px-4 sm:px-8 no-print">
        <div className="max-w-6xl mx-auto">
          <Link to="/education" className="inline-flex items-center gap-2 text-saffron text-xs uppercase tracking-widest hover:text-saffron-light mb-5">
            <ArrowLeft size={14} /> Back to Education
          </Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-saffron">{META.series}</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">{META.title}</h1>
          <p className="text-pearl/70 mt-2">{META.subtitle}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => window.print()}
              data-testid="ks2-workbook-print"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron/90"
            >
              <Printer size={16} /> Print this worksheet
            </button>
            <Link
              to="/education/ks2-book"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 border border-white/20 text-pearl rounded-md text-sm hover:bg-white/15"
            >
              <BookOpen size={16} /> Open Student Book
            </Link>
            <Link
              to="/education/ks2-teacher-guide"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 border border-white/20 text-pearl rounded-md text-sm hover:bg-white/15"
            >
              <ListChecks size={16} /> Teacher Guide
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        {/* Sidebar, chapter index */}
        <aside className="no-print">
          <div className="lg:sticky lg:top-24">
            <div className="text-[10px] uppercase tracking-widest text-saffron mb-3">Worksheets</div>
            <ul className="space-y-1.5" data-testid="ks2-workbook-index">
              {WORKSHEETS.map((w) => (
                <li key={w.n}>
                  <button
                    onClick={() => setActive(w.n)}
                    data-testid={`ks2-workbook-link-${w.n}`}
                    className={`w-full text-left text-sm px-3 py-2 rounded-md transition-colors ${
                      active === w.n
                        ? "bg-navy text-pearl"
                        : "text-navy/70 hover:bg-saffron/10 hover:text-saffron"
                    }`}
                  >
                    <span className="font-display text-saffron mr-2">{w.n}</span>
                    {w.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Worksheet */}
        <article className="bg-white rounded-lg border border-navy/10 shadow-sm p-6 sm:p-10 print-section" data-testid={`ks2-worksheet-${current.n}`}>
          <div className="border-b border-navy/10 pb-5 mb-6">
            <p className="text-[10px] uppercase tracking-widest text-saffron">Worksheet {current.n} of {WORKSHEETS.length}</p>
            <h2 className="font-display text-3xl text-navy mt-1">{current.title}</h2>
            {current.intro && (
              <p className="mt-3 text-sm text-navy/65 italic">{current.intro}</p>
            )}
            <div className="mt-5 grid grid-cols-2 gap-4 text-xs text-navy/65">
              <div><span className="uppercase tracking-widest text-saffron">Name</span><div className="border-b border-navy/30 mt-2 h-5" /></div>
              <div><span className="uppercase tracking-widest text-saffron">Date</span><div className="border-b border-navy/30 mt-2 h-5" /></div>
            </div>
          </div>

          <div className="space-y-5">
            {current.tasks.map((t, i) => (
              <TaskBlock key={i} task={t} idx={i} />
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-navy/10 flex justify-between text-xs text-navy/50">
            <span>{META.title}</span>
            <span>{META.edition}</span>
          </div>
        </article>
      </div>
    </div>
  );
}
